import React, { useState, useRef, useEffect } from "react";
import { cva } from "class-variance-authority";
import clsx from "clsx";

// PopOver 定位样式
const popoverStyles = cva(
  "absolute z-50 opacity-0 transition-all duration-[200ms] ease-out pointer-events-none",
  {
    variants: {
      anchor: {
        "top-right": "top-full right-0 mt-2",
        "top-left": "top-full left-0 mt-2",
        middle: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      },
      visible: {
        true: "opacity-100 translate-y-1 duration-[200ms] ease-in pointer-events-auto",
        false: ""
      }
    },
    defaultVariants: {
      anchor: "top-right",
      visible: false
    }
  }
);

// Group hover 弹窗样式 (模仿 ApplicationPopper 的逻辑)
const groupHoverPopoverStyles = cva(
  [
    "absolute z-50 scale-100",
    // 动画效果
    "invisible group-hover:visible",
    "opacity-0 transition-all duration-[200ms] ease-in-out origin-top-right",
    "group-hover:opacity-100 group-hover:translate-y-1",
    // 交互控制
    "pointer-events-none group-hover:pointer-events-auto",
    // CSS-only hover bridge using ::before pseudo-element
    "before:absolute before:invisible before:group-hover:visible before:pointer-events-none before:group-hover:pointer-events-auto"
  ],
  {
    variants: {
      anchor: {
        "top-right": [
          "top-[64px] right-0",
          "before:top-[-10px] before:right-0 before:w-full before:h-[10px]"
        ],
        "top-left": [
          "top-[64px] left-0",
          "before:top-[-10px] before:left-0 before:w-full before:h-[10px]"
        ],
        middle: [
          "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          "before:top-[-50%] before:left-1/2 before:-translate-x-1/2 before:w-full before:h-[10px]"
        ]
      }
    },
    defaultVariants: {
      anchor: "top-right"
    }
  }
);

// Group hover 容器样式，添加合理的 padding 来扩展 hover 区域
const groupHoverContainerStyles = cva("relative group", {
  variants: {
    anchor: {
      "top-right": "pb-0", // 在底部添加小的 padding 来扩展 hover 区域
      "top-left": "pb-0",
      middle: ""
    }
  },
  defaultVariants: {
    anchor: "top-right"
  }
});

interface PopOverProps {
  trigger?: "hover" | "click" | "group-hover";
  children: React.ReactNode;
  content: React.ReactNode;
  anchor?: "top-right" | "top-left" | "middle";
  className?: string;
  containerClassName?: string;
}

const PopOver: React.FC<PopOverProps> = ({
  trigger = "hover",
  children,
  content,
  anchor = "top-right",
  className,
  containerClassName
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Click 外部关闭
  useEffect(() => {
    if (trigger !== "click") return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, trigger]);

  // Hover 处理
  const handleMouseEnter = () => {
    if (trigger !== "hover") return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (trigger !== "hover") return;

    setIsHovered(false);
    // 延迟隐藏，允许鼠标移动到弹窗内容上
    timeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsVisible(false);
      }
    }, 100);
  };

  // Click 处理
  const handleClick = () => {
    if (trigger !== "click") return;
    setIsVisible(!isVisible);
  };

  // PopOver 内容的鼠标事件（仅hover模式）
  const handlePopoverMouseEnter = () => {
    if (trigger !== "hover") return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  };

  const handlePopoverMouseLeave = () => {
    if (trigger !== "hover") return;

    setIsHovered(false);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const triggerProps =
    trigger === "hover"
      ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave
        }
      : {
          onClick: handleClick
        };

  const popoverProps =
    trigger === "hover"
      ? {
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave
        }
      : {};

  // 如果是 group-hover 模式，直接返回使用 CSS group hover 的结构
  return trigger === "group-hover" ? (
    <div
      ref={containerRef}
      className={clsx(groupHoverContainerStyles({ anchor }), containerClassName)}
    >
      {/* 触发元素 */}
      {children}
      {/* PopOver 内容 - 使用 CSS group hover，包含连接区域 */}
      <div className={clsx(groupHoverPopoverStyles({ anchor }), className)}>{content}</div>
    </div>
  ) : (
    <div
      ref={containerRef}
      className={clsx("relative", "[&>div:nth-child(1)]:h-full", containerClassName)}
      {...triggerProps}
    >
      {/* 触发元素 */}
      {children}
      {/* PopOver 内容 */}
      <div
        className={clsx(popoverStyles({ anchor, visible: isVisible }), className)}
        {...popoverProps}
      >
        {content}
      </div>
    </div>
  );
};

export default PopOver;
